import { AsyncStockRequest } from '../model/async-stock-request';
import QuantitySelector from '../components/inputs/quantity-selector';
import ProductFlags from '../model/product-flags';

// eslint-disable-next-line import/prefer-default-export
export class RowReader {
  static requestFromRow(el: HTMLElement): AsyncStockRequest | null {
    const {
      code, productVariantCodes, productRoundingQuantity, isMasterClass, entryNumber,
    } = el.dataset;
    const quantity = RowReader.quantityFor(el) ?? productRoundingQuantity;
    if (!code || !quantity) {
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions, no-console
      console.error(`Insufficient details for async product request, productCode: ${code}, quantity: ${quantity}`);
      return null;
    }
    return {
      productCode: code,
      productVariantCodes,
      quantity,
      isMasterClass: isMasterClass === 'true',
      entryNumber,
    };
  }

  static requestsFromRows(rows: JQuery): AsyncStockRequest[] {
    const result: AsyncStockRequest[] = [];
    rows.each((_, el) => {
      const req = RowReader.requestFromRow(el);
      if (req) result.push(req);
    });
    return result;
  }

  /**
   * Selects descendants matching `selector` that belong to this row only,
   * skipping any nested `.js-asyncProductData` subtrees (e.g. matching tires
   * rendered inside a parent product row). Without this, a `row.find(...)`
   * from a parent row leaks into the nested tires and updates the wrong elements.
   */
  static ownDescendants(row: JQuery, selector: string): JQuery {
    const rowEl = row[0];
    return row
      .find(selector)
      .filter((_, el) => $(el).closest('.js-asyncProductData')[0] === rowEl);
  }

  static resolveRow(
    rows: JQuery,
    productCode: string,
    codeCount: Record<string, number>,
  ): JQuery {
    let row = rows.filter(`[data-code="${productCode}"]`);
    if (row.length > 1) {
      const idx = codeCount[productCode] ?? 0;
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      row = $(row.get(idx)!);
      // eslint-disable-next-line no-param-reassign
      codeCount[productCode] = idx + 1;
    }
    return row;
  }

  static readProductFlags(row: JQuery): ProductFlags {
    return {
      isArchived: row.data('isArchived') as boolean,
      isDirectDelivery: row.data('isDirectDelivery') as boolean,
      isMasterClass: row.data('isMasterClass') as boolean,
      isPhasedOut: row.data('isPhasedOut') as boolean,
      isTireOrWheel: (row.data('isTire') as boolean) || (row.data('isWheel') as boolean),
      hasSubstitution: row.data('hasSubstitution') as boolean,
    };
  }

  static quantityFor(el: HTMLElement): number | null {
    const { entryNumber, code } = el.dataset;
    if (entryNumber) return QuantitySelector.getValueForEntryNumber(entryNumber, $(el));
    if (code) return QuantitySelector.getValueForProductCode(code, $(el));
    return null;
  }

  static qtyInputFor(el: HTMLElement): JQuery<HTMLInputElement> | null {
    const { entryNumber, code } = el.dataset;
    if (entryNumber) return QuantitySelector.getInputForEntryNumber(entryNumber, $(el));
    if (code) return QuantitySelector.getInputForProductCode(code, $(el));
    return null;
  }
}
